import { Component, OnInit, OnDestroy, AfterViewInit, inject, ChangeDetectorRef, PLATFORM_ID, Inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import Swal from 'sweetalert2';
import { CartService, TicketDetailed, EventoInfo } from '../../core/services/cart.service';
import { GuestSession } from '../../core/services/guest-session';
import { UsersService } from '../../core/services/users';
import { FacturacionService, FacturacionData, ContactoInfo, InformacionFactura, TicketFormData } from '../../core/services/facturacion.service';
import { AuthService, ProfileResponse } from '../../core/services/auth.service';
import { WebAppConfigService } from '../../core/services/webapp-config.service';
import { API_CONFIG } from '../../core/config/api.config';
import { COUNTRIES, Country, validatePhoneNumber, getPhoneValidationMessage, formatPhoneNumber, parsePhoneNumber } from '../../core/config/countries.config';
import { PAYPHONE_CONFIG } from '../../core/config/payphone.config';
import { MetaService } from '../../core/services/meta.service';

declare global {
  interface Window {
    PPaymentButtonBox: any;
  }
}

interface TipoIdentificacion {
  codigo: string;
  descripcion: string;
  validacion: string | null;
  activo: boolean;
}

interface TipoIdentificacionResponse {
  success: boolean;
  message: string;
  data: TipoIdentificacion[];
  timestamp: string;
}

interface MetodoPago {
  metodopagoid: string;
  eventoid: string;
  tipo: string;
  nombre: string;
  descripcion?: string | null;
  comision: number;
  estado: string;
  canales?: MetodoPagoCanal[];
  createdAt: string;
  updatedAt: string;
  secret_key?: string;
  appId?: string;
  tokenId?: string;
}

interface MetodoPagoCanal {
  canal: string;
  disponible: boolean;
}

interface MetodoPagoResponse {
  success: boolean;
  message: string;
  data: MetodoPago[];
  timestamp: string;
}

interface LegalDocs {
  eventoid: string;
  terminosCondiciones: string;
  politicaDatos: string;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

interface LegalDocsResponse {
  success: boolean;
  message: string;
  data: LegalDocs;
  timestamp: string;
}

interface BillingInfo {
  nombreCompleto: string;
  tipo_identificacion: {
    codigo: string;
    descripcion: string;
  };
  direccion: string;
  telefono: string; // Solo el número sin código de país
  email: string;
  identificacion: string;
}

interface UserInfo {
  firstname: string;
  lastname: string;
  email: string;
  phone: string; // Solo el número sin código de país
}

interface TicketGroup {
  tipoticketid: string;
  nombreLocalidad: string;
  nombrePrecio: string;
  cantidad: number;
  precioBase: number;
  ivaTicket: number;
  precioTicketFinal: number;
  cargoServicio: number;
  ivaCargoServicio: number;
  cargoServicioFinal: number;
  servicioAdministrativo: number;
  ivaServicioAdministrativo: number;
  servicioAdministrativoFinal: number;
  precioTotal: number;
  subtotal: number;
  nombreMoneda: string;
  nombreSubevento?: string;
  funcionFecha?: string;
  funcionHora?: string;
  tipoprecio?: string;
}

interface OrderTotals {
  subtotalBase: number;
  totalIvaTickets: number;
  totalCargoServicio: number;
  totalIvaCargoServicio: number;
  totalServicioAdministrativo: number;
  totalIvaServicioAdministrativo: number;
  totalGeneral: number;
  nombreMoneda: string;
}

interface FormField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  order: number;
  defaultValue?: string;
  options?: string[];
  placeholder?: string;
  maxlength?: number;  // Longitud máxima del campo
  min?: number;        // Valor mínimo (para number)
  max?: number;        // Valor máximo (para number)
  helpText?: string;   // Texto de ayuda
  validation?: {
    minLength?: number;
    maxLength?: number;
    pattern?: string;
  };
}

interface ConditionalValidationRule {
  when: {
    field: string;
    value: string;
  };
  target: {
    field: string;
  };
  enforcement: {
    onlyNumbers?: boolean;
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
    regexType?: 'none' | 'numbers' | 'letters' | 'alphanumeric' | 'custom';
    pattern?: string;
    message?: string;
  };
  active?: boolean;
}

interface TemplateForm {
  templateformid: string;
  nombre: string;
  descripcion: string;
  slug: string;
  formfields: FormField[];
  conditionalValidations?: ConditionalValidationRule[];
  createdAt: string;
}

interface EventoForm {
  eventoid: string;
  templateformid: string;
  order: number;
  createdAt: string;
  updatedAt: string;
  templateForm: TemplateForm;
}

interface EventoFormResponse {
  success: boolean;
  data: EventoForm[];
  count: number;
}

interface EffectiveFieldConstraints {
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  onlyNumbers: boolean;
  message?: string;
}

@Component({
  selector: 'app-checkout',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './checkout.html',
  styleUrls: ['./checkout.css']
})
export class CheckoutComponent implements OnInit, AfterViewInit, OnDestroy {
  private checkingEmail = false;
  private cartService = inject(CartService);
  private guestSession = inject(GuestSession);
  private usersService = inject(UsersService);
  private facturacionService = inject(FacturacionService);
  private authService = inject(AuthService);
  private webAppConfig = inject(WebAppConfigService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private metaService = inject(MetaService);

  urlSlug: string = '';

  // Track if data is loaded from profile (to make fields readonly)
  isProfileDataLoaded: boolean = false;
  isBillingDataLoaded: boolean = false;
  currentUserId: string | null = null;

  // Label del servicio de emisión desde configuración
  servicioEmisionLabel: string = API_CONFIG.labels.servicioEmisionTickets;

  // Logo dinámico desde configuración webapp
  get logoUrl(): string {
    return this.webAppConfig.getLogoUrl();
  }

  cartItems: TicketDetailed[] = [];
  eventInfo: EventoInfo | null = null;
  totalAmount: number = 0;
  cartId: string = '';
  paymentId: string = ''; // ID de pago generado
  expiresAt: string = '';
  timeRemaining: string = '';
  private countdownInterval: any;
  cartData: any = null; // Almacena la respuesta completa del carrito incluyendo sri_totales

  // Tickets agrupados y totales
  ticketGroups: TicketGroup[] = [];
  orderTotals: OrderTotals = {
    subtotalBase: 0,
    totalIvaTickets: 0,
    totalCargoServicio: 0,
    totalIvaCargoServicio: 0,
    totalServicioAdministrativo: 0,
    totalIvaServicioAdministrativo: 0,
    totalGeneral: 0,
    nombreMoneda: 'USD'
  };

  // User contact info
  userInfo: UserInfo = {
    firstname: '',
    lastname: '',
    email: '',
    phone: '' // Solo el número, se combinará con el código del país al enviar
  };

  // Email confirmation for non-logged users
  emailConfirmation: string = '';

  // Billing info
  billingInfo: BillingInfo = {
    nombreCompleto: '',
    tipo_identificacion: {
      codigo: '',
      descripcion: ''
    },
    direccion: '',
    telefono: '', // Solo el número, se combinará con el código del país al enviar
    email: '',
    identificacion: ''
  };

  // Tipos de identificación
  tiposIdentificacion: TipoIdentificacion[] = [];
  isLoadingTiposId: boolean = false;

  // Lista de países disponibles
  countries: Country[] = COUNTRIES;
  showCountryDropdownContact: boolean = false;
  showCountryDropdownBilling: boolean = false;
  // Códigos de país seleccionados (separados de los datos de usuario/facturación)
  selectedCountryContact: string = 'EC'; // Ecuador por defecto
  selectedCountryBilling: string = 'EC'; // Ecuador por defecto

  // Métodos de pago
  metodosPago: MetodoPago[] = [];
  isLoadingMetodosPago: boolean = false;
  showPaymentModal: boolean = false;
  selectedMetodoPago: MetodoPago | null = null;

  // Legal documents
  legalDocs: LegalDocs | null = null;
  isLoadingLegalDocs: boolean = false;
  aceptaTerminos: boolean = false;
  aceptaPoliticaDatos: boolean = false;
  showTerminos: boolean = false;
  showPoliticaDatos: boolean = false;

  isLoadingCart: boolean = false;
  isLoading: boolean = false;

  // Comisión del método de pago
  comisionMetodoPago: number = 0; // Monto de la comisión
  porcentajeComision: number = 0; // Porcentaje de comisión
  totalConComision: number = 0; // Total incluyendo comisión

  // Formularios del evento
  eventoForms: EventoForm[] = [];
  isLoadingForms: boolean = false;
  ticketFormsData: TicketFormData[] = []; // Array de formularios por ticket

  // Modal cambio de precio
  showChangePriceModal: boolean = false;
  ticketForPriceChange: TicketDetailed | null = null;
  availablePricesForChange: any[] = [];
  selectedNewPrice: any = null;
  isLoadingPricesForChange: boolean = false;
  isChangingPrice: boolean = false;

  private rendered = false;
  private paymentBox: any;
  private renderRetries = 0;
  private maxRenderRetries = 5;
  private ppFormObserver: MutationObserver | null = null;
  private extendTimeDebounceTimer: any = null;
  private ppContainerEl: HTMLElement | null = null;
  private ppPayClickHandler: ((event: Event) => void) | null = null;
  private ppPaySubmitHandler: ((event: Event) => void) | null = null;
  private payphonePayHandled: boolean = false;

  goToHome(): void {
    this.router.navigate(['/']);
  }

  ngOnInit() {
    const config = this.webAppConfig.config();
    if (config) {
      this.metaService.resetToDefault(
        this.webAppConfig.getSiteName() + ' - ' + config.short_description,
        config.description,
        config.url_logo,
        config.url
      );
    }

    this.urlSlug = this.route.snapshot.paramMap.get('url_slug') || '';

    // Limpiar datos de facturación al entrar al checkout
    // En modo cookie, sessionId será null (esperado)
    if (this.guestSession.isSessionActive()) {
      const sessionId = this.guestSession.getSessionId();
      this.facturacionService.clearIfCartOrSessionChanged('', sessionId || '');
    }

    // Load user profile if logged in
    this.loadUserProfile();

    this.loadCartItems();
    this.loadTiposIdentificacion();

    // Agregar listener para cerrar dropdowns al hacer clic fuera
    document.addEventListener('click', this.closeDropdownsOnClickOutside.bind(this));
  }

  closeDropdownsOnClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    // Cerrar dropdowns si se hace clic fuera de ellos
    if (!target.closest('.country-selector')) {
      this.showCountryDropdownContact = false;
      this.showCountryDropdownBilling = false;
    }
  }

  ngAfterViewInit() {
    // No cargar PayPhone automáticamente aquí porque el contenedor 
    // solo existe en el paso 'billing'
    const container = document.getElementById('pp-button');
    if (container) {
      container.innerHTML = '';
    }
  }
  loadPayPhoneButton() {
    if (this.rendered) return;

    // Verificar que el contenedor existe antes de intentar renderizar
    const container = document.getElementById('pp-button');

    if (!container) {
      console.error('❌ Contenedor para PayPhone no encontrado');
      return;
    }

    if (!this.selectedMetodoPago) {
      console.error('❌ No hay método de pago seleccionado');
      return;
    }

    try {
      // Limpia el contenedor completamente
      container.innerHTML = '';

      // Verificar que PPaymentButtonBox está disponible
      if (typeof window.PPaymentButtonBox === 'undefined') {
        console.error('❌ PPaymentButtonBox no está disponible');
        return;
      }

      // Obtener credenciales de PayPhone del método de pago
      // tokenId: Token largo de autenticación de PayPhone
      // appId: Store ID de PayPhone
      const token = this.selectedMetodoPago.tokenId || '';
      const storeId = this.selectedMetodoPago.appId || '';

      if (!token || !storeId) {
        console.error('❌ Faltan credenciales de PayPhone en el método de pago');
        console.log('Método de pago completo:', this.selectedMetodoPago);
        console.log('Token encontrado:', token ? 'Sí (' + token.substring(0, 20) + '...)' : 'No');
        console.log('StoreId encontrado:', storeId || 'No');
        return;
      }

      // Calcular comisión del método de pago
      const comisionPorcentaje = this.selectedMetodoPago.comision || 0;
      const totalDeTodo = this.orderTotals.subtotalBase + this.orderTotals.totalCargoServicio + this.orderTotals.totalIvaTickets + this.orderTotals.totalIvaCargoServicio + this.orderTotals.totalServicioAdministrativo + this.orderTotals.totalIvaServicioAdministrativo;
      const servicePayphoneAmount = (totalDeTodo * comisionPorcentaje) / 100;

      // Guardar comisión en propiedades del componente
      this.porcentajeComision = comisionPorcentaje;
      this.comisionMetodoPago = servicePayphoneAmount;
      this.totalConComision = totalDeTodo + servicePayphoneAmount;

      // Para PayPhone - El total a pagar incluye la comisión
      // Enviamos todo como "sin impuestos" porque ya están calculados en el total
      const amountWithTax = 0;
      const amountWithoutTax = totalDeTodo + servicePayphoneAmount;
      const totalTax = 0;

      // El amount debe ser igual a la suma de amountWithTax + amountWithoutTax + tax
      const totalAmount = amountWithTax + amountWithoutTax + totalTax;

      // Validación: amount = amountWithTax + amountWithoutTax + tax
      const calculatedAmount = amountWithTax + amountWithoutTax + totalTax;

      // PayPhone requiere amounts en centavos (enteros)
      const amountInCents = Math.round(totalAmount * 100);
      const amountWithTaxInCents = Math.round(amountWithTax * 100);
      const amountWithoutTaxInCents = Math.round(amountWithoutTax * 100);
      const taxInCents = Math.round(totalTax * 100);

      // Nuevo flujo: clientTransactionId debe ser el cartid efectivo
      const transactionId = this.cartId;

      console.log('💰 Configurando PayPhone con:');
      console.log('  - Token:', token.substring(0, 30) + '...');
      console.log('  - StoreId:', storeId);
      console.log('  - Transaction ID (paymentId):', transactionId);
      console.log('  � Comisión del método de pago:');
      console.log('    * Porcentaje:', this.porcentajeComision + '%');
      console.log('    * Monto comisión:', this.comisionMetodoPago.toFixed(2), 'USD');
      console.log('    * Total con comisión:', this.totalConComision.toFixed(2), 'USD');
      console.log('  �📊 Montos en USD:');
       console.log('    * Subtotal base tickets:', this.orderTotals.subtotalBase);
      console.log('    * IVA tickets:', this.orderTotals.totalIvaTickets);
      console.log('    * Cargo servicio base:', this.orderTotals.totalCargoServicio);
      console.log('    * IVA cargo servicio:', this.orderTotals.totalIvaCargoServicio);
      console.log('    * Servicio administrativo base:', this.orderTotals.totalServicioAdministrativo);
      console.log('    * IVA servicio administrativo:', this.orderTotals.totalIvaServicioAdministrativo);
      console.log('  🧮 Cálculos PayPhone:');
      console.log('    * amountWithTax (base con IVA):', amountWithTax, 'USD');
      console.log('    * amountWithoutTax (base sin IVA):', amountWithoutTax, 'USD');
      console.log('    * tax:', totalTax, 'USD');
      console.log('    * Total calculado (amountWithTax + amountWithoutTax + tax):', calculatedAmount, 'USD');
      console.log('    * amount (total general):', totalAmount, 'USD');
      console.log('  💵 Montos en centavos:');
      console.log('    * amount:', amountInCents);
      console.log('    * amountWithTax:', amountWithTaxInCents);
      console.log('    * amountWithoutTax:', amountWithoutTaxInCents);
      console.log('    * tax:', taxInCents);
      console.log('  ✅ Validación: amountWithTax + amountWithoutTax + tax =', amountWithTaxInCents + amountWithoutTaxInCents + taxInCents, '(debe ser igual a amount:', amountInCents + ')');

      /* this.paymentBox = new window.PPaymentButtonBox({
        token: token,
        clientTransactionId: transactionId,
        storeId: storeId,
        reference: this.eventInfo?.nombre || 'Evento sin nombre',
        amount: amountInCents,
        amountWithTax: amountWithTaxInCents,
        amountWithoutTax: amountWithoutTaxInCents,
        tax: taxInCents,
        currency: this.orderTotals.nombreMoneda
      });
      this.paymentBox.render('pp-button');
      this.rendered = true;
      console.log('✅ Botón de PayPhone renderizado correctamente');
      // Extender carrito a 10 min cuando se renderiza PayPhone
      this.extendCartTimeWithKeepalive(10);
      // Comenzar a observar el formulario para extender el tiempo del carrito
      this.startPayphoneFormWatcher(); */

      const body = {
        amount: amountInCents,
        amountWithTax: amountWithTaxInCents,
        amountWithoutTax: amountWithoutTaxInCents,
        tax: taxInCents,

        reference: this.eventInfo?.nombre || 'Evento sin nombre',
        currency: this.orderTotals.nombreMoneda,

        clientTransactionId: transactionId,
        storeId: storeId,

        ResponseUrl: PAYPHONE_CONFIG.confirmUrl,
        cancellationUrl: PAYPHONE_CONFIG.cancelUrl
      };

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      fetch(PAYPHONE_CONFIG.prepareUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      })
        .then(response => response.json())
        .then((response: any) => {
          console.log(response);

          // Abrir directamente formulario de tarjeta
          if (response.payWithCard) {
            this.extendCartTimeWithKeepalivePrecision(10, 10);
            window.location.href = response.payWithCard;
          }
        })
        .catch((err) => {
          console.error('Error preparando pago', err);
        });
    } catch (error) {
      console.error('❌ Error al renderizar botón de PayPhone:', error);
      this.rendered = false;
    }
  }

  /**
   * Observa #pp-button hasta que Payphone inyecte su formulario.
   * - Al aparecer el formulario: resetea el carrito a exactamente 5 min (una sola vez).
   * - Al hacer click en "Pagar": resetea de nuevo a 5 min exactos (tiempo de confirmación Payphone).
   */
  private startPayphoneFormWatcher(): void {
    const container = document.getElementById('pp-button');
    if (!container) {
      console.warn('🧪 [PayphoneDebug] #pp-button no encontrado, watcher no inicializado');
      return;
    }

    console.log('🧪 [PayphoneDebug] Iniciando watcher Payphone', {
      cartId: this.cartId,
      hasSubmitNow: !!container.querySelector('button[type="submit"], .ppb-btn-pay'),
      hasFormNow: !!container.querySelector('form')
    });

    this.stopPayphoneFormWatcher();
    this.ppContainerEl = container;
    this.payphonePayHandled = false;

    const triggerPayReset = (source: string) => {
      if (this.payphonePayHandled) {
        console.log('🧪 [PayphoneDebug] triggerPayReset ignorado por deduplicación', { source });
        return;
      }
      this.payphonePayHandled = true;
      console.log(`🛒 ${source} — click en PayPhone`);
      // Only dedupe the immediate click+submit pair; allow later Payphone steps.
      setTimeout(() => {
        this.payphonePayHandled = false;
        console.log('🧪 [PayphoneDebug] Ventana de deduplicación cerrada, se permiten nuevos triggers');
      }, 1500);
    };

    const attachSubmitListener = (root: HTMLElement) => {
      const isPagarAction = (element: HTMLElement | null): boolean => {
        if (!element) return false;
        const text = (element.textContent || '').trim().toLowerCase();
        return text.includes('pagar');
      };

      console.log('🧪 [PayphoneDebug] Adjuntando listeners de click/submit al contenedor', {
        forms: root.querySelectorAll('form').length,
        submitButtons: root.querySelectorAll('button[type="submit"], .ppb-btn-pay').length
      });

      this.ppPayClickHandler = (event: Event) => {
        const target = event.target as HTMLElement | null;
        const payButton = target?.closest('button[type="submit"], .ppb-btn-pay, .ppb-button[type="submit"]');
        if (!payButton) return;
        if (!root.contains(payButton)) return;
        if (!isPagarAction(payButton as HTMLElement)) {
          console.log('🧪 [PayphoneDebug] Click capturado pero ignorado (no es Pagar)', {
            text: ((payButton as HTMLElement).textContent || '').trim(),
            className: (payButton as HTMLElement).className
          });
          return;
        }
        console.log('🧪 [PayphoneDebug] Click capturado en posible botón Pagar', {
          tag: payButton.tagName,
          className: payButton.className,
          text: (payButton.textContent || '').trim()
        });
        triggerPayReset('Click en Pagar');
      };

      this.ppPaySubmitHandler = (event: Event) => {
        const form = event.target as HTMLElement | null;
        if (!form) return;
        if (!root.contains(form)) return;
        if (form.tagName?.toLowerCase() !== 'form') return;

        const submitEvent = event as any;
        const submitter = submitEvent?.submitter as HTMLElement | null;
        if (submitter && !isPagarAction(submitter)) {
          console.log('🧪 [PayphoneDebug] Submit capturado pero ignorado (submitter no es Pagar)', {
            text: (submitter.textContent || '').trim(),
            className: submitter.className
          });
          return;
        }

        console.log('🧪 [PayphoneDebug] Submit capturado en formulario Payphone');
        triggerPayReset('Submit en formulario Payphone');
      };

      // Capture mode survives dynamic DOM replacements by the Payphone SDK.
      root.addEventListener('click', this.ppPayClickHandler, true);
      root.addEventListener('submit', this.ppPaySubmitHandler, true);
    };

    const onFormDetected = (root: HTMLElement) => {
      console.log('🛒 Formulario Payphone detectado');
      console.log('🧪 [PayphoneDebug] onFormDetected', {
        forms: root.querySelectorAll('form').length,
        submitButtons: root.querySelectorAll('button[type="submit"], .ppb-btn-pay').length
      });
      attachSubmitListener(root);
    };

    if (container.querySelector('input, button[type="submit"]')) {
      onFormDetected(container);
      return;
    }

    this.ppFormObserver = new MutationObserver(() => {
      console.log('🧪 [PayphoneDebug] MutationObserver ejecutado en #pp-button');
      if (container.querySelector('input, button[type="submit"]')) {
        console.log('🧪 [PayphoneDebug] Submit/input detectado por observer, conectando listeners');
        this.ppFormObserver?.disconnect();
        this.ppFormObserver = null;
        onFormDetected(container);
      }
    });

    this.ppFormObserver.observe(container, { childList: true, subtree: true });
  }

  private stopPayphoneFormWatcher(): void {
    this.ppFormObserver?.disconnect();
    this.ppFormObserver = null;
    if (this.ppContainerEl && this.ppPayClickHandler) {
      this.ppContainerEl.removeEventListener('click', this.ppPayClickHandler, true);
    }
    if (this.ppContainerEl && this.ppPaySubmitHandler) {
      this.ppContainerEl.removeEventListener('submit', this.ppPaySubmitHandler, true);
    }
    this.ppPayClickHandler = null;
    this.ppPaySubmitHandler = null;
    this.ppContainerEl = null;
    this.payphonePayHandled = false;
    if (this.extendTimeDebounceTimer) {
      clearTimeout(this.extendTimeDebounceTimer);
      this.extendTimeDebounceTimer = null;
    }
  }

  private scheduleExtendCartTime(): void {
    if (this.extendTimeDebounceTimer) clearTimeout(this.extendTimeDebounceTimer);
    this.extendTimeDebounceTimer = setTimeout(() => this.extendCartTime(), 3000);
  }

  private extendCartTime(minutes: number = 5): void {
    if (!this.cartId) return;
    const endpoint = API_CONFIG.endpoints.carrito.extendTime.replace('{cartid}', this.cartId);
    this.http.post(
      `${API_CONFIG.baseUrl}${endpoint}`,
      { minutes },
      { headers: { 'x-api-key': API_CONFIG.api_key } }
    ).subscribe({
      next: (res: any) => console.log(`⏱️ Carrito reseteado a ${minutes} min, expiresAt:`, res?.data?.expiresAt),
      error: (err) => console.warn('⚠️ No se pudo extender el tiempo del carrito:', err?.status)
    });
  }

  private extendCartTimeWithKeepalive(minutes: number = 10): void {
    if (!this.cartId) {
      console.warn('🧪 [PayphoneDebug] extendCartTimeWithKeepalive cancelado: cartId vacío');
      return;
    }
    const endpoint = API_CONFIG.endpoints.carrito.extendTime.replace('{cartid}', this.cartId);
    const url = `${API_CONFIG.baseUrl}${endpoint}`;
    console.log('🧪 [PayphoneDebug] Enviando keepalive extend-time', {
      url,
      cartId: this.cartId,
      minutes
    });

    // keepalive allows the request to complete even if Payphone redirects immediately.
    fetch(url, {
      method: 'POST',
      headers: {
        'x-api-key': API_CONFIG.api_key,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ minutes }),
      keepalive: true
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        try {
          const data = await response.json();
          const newExpiresAt = data?.data?.expiresAt;
          console.log(`⏱️ [keepalive] Carrito reseteado a ${minutes} min, expiresAt:`, newExpiresAt);
          if (newExpiresAt) {
            this.expiresAt = newExpiresAt;
            this.startCountdown(this.expiresAt);
            this.cdr.detectChanges();
          }
        } catch {
          console.log(`⏱️ [keepalive] Carrito reseteado a ${minutes} min`);
        }
      })
      .catch((error) => {
        console.warn('⚠️ [keepalive] No se pudo extender el tiempo del carrito:', error?.message || error);
      });
  }

  private extendCartTimeWithKeepalivePrecision(minutes: number, seconds: number): void {
    if (!this.cartId) {
      console.warn('🧪 [PayphoneDebug] extendCartTimeWithKeepalivePrecision cancelado: cartId vacío');
      return;
    }
    const endpoint = API_CONFIG.endpoints.carrito.extendTimePrecise.replace('{cartid}', this.cartId);
    const url = `${API_CONFIG.baseUrl}${endpoint}`;
    console.log('🧪 [PayphoneDebug] Enviando keepalive extend-time-precise', {
      url,
      cartId: this.cartId,
      minutes,
      seconds
    });

    fetch(url, {
      method: 'POST',
      headers: {
        'x-api-key': API_CONFIG.api_key,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ minutes, seconds }),
      keepalive: true
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        try {
          const data = await response.json();
          const newExpiresAt = data?.data?.expiresAt;
          console.log(`⏱️ [keepalive-precise] Carrito reseteado a ${minutes}m ${seconds}s, expiresAt:`, newExpiresAt);
          if (newExpiresAt) {
            this.expiresAt = newExpiresAt;
            this.startCountdown(this.expiresAt);
            this.cdr.detectChanges();
          }
        } catch {
          console.log(`⏱️ [keepalive-precise] Carrito reseteado a ${minutes}m ${seconds}s`);
        }
      })
      .catch((error) => {
        console.warn('⚠️ [keepalive-precise] No se pudo extender el tiempo del carrito:', error?.message || error);
      });
  }

  ngOnDestroy() {
    console.log('🧹 Limpiando componente checkout...');
    this.stopPayphoneFormWatcher();
    // Cleanup cuando el componente se destruye
    this.clearCountdown();
    this.isLoadingCart = false;
    this.isLoading = false;
    this.checkingEmail = false;
    this.rendered = false;
    this.comisionMetodoPago = 0;
    this.porcentajeComision = 0;
    this.totalConComision = 0;

    // Solo ejecutar código del DOM en el navegador
    if (isPlatformBrowser(this.platformId)) {
      // Limpiar el botón de Payphone si existe
      if (this.paymentBox) {
        try {
          // Intentar destruir el objeto de Payphone
          if (typeof this.paymentBox.destroy === 'function') {
            this.paymentBox.destroy();
          }
          this.paymentBox = null;
        } catch (error) {
          console.error('Error al destruir PayPhone:', error);
        }
      }

      // Limpiar el contenedor del DOM
      const container = document.getElementById('pp-button');
      if (container) {
        container.innerHTML = '';
      }

      // Remover listener de clics
      document.removeEventListener('click', this.closeDropdownsOnClickOutside.bind(this));
    }

    console.log('✅ Limpieza completada');
  }

  loadUserProfile() {
    // Cuando cookie_http_only es true, no podemos verificar localmente si hay sesión
    // Siempre intentamos llamar a /auth/me para validar la sesión en el servidor
    console.log('🔍 Verificando sesión de usuario con /auth/me...');

    this.authService.getProfile().subscribe({
      next: (response: ProfileResponse) => {
        if (response.success && response.data) {
          console.log('✅ Usuario autenticado - Perfil cargado:', response.data);

          const profile = response.data.profile;
          this.currentUserId = profile.userid;

          // Actualizar el signal del usuario en AuthService si está disponible
          this.authService.setCurrentUserFromProfile(profile);

          // Pre-fill contact info
          // Separar código de país y número si viene en formato completo
          let phoneNumber = profile.phone || '';
          if (phoneNumber && phoneNumber.startsWith('+')) {
            const parsed = parsePhoneNumber(phoneNumber);
            if (parsed) {
              this.selectedCountryContact = parsed.countryCode;
              phoneNumber = parsed.phoneNumber;
              console.log('📞 Teléfono de contacto parseado:', { country: parsed.countryCode, phone: parsed.phoneNumber });
            }
          }

          this.userInfo = {
            firstname: profile.firstname,
            lastname: profile.lastname,
            email: profile.email,
            phone: phoneNumber
          };
          this.isProfileDataLoaded = true;

          // Pre-fill billing info if exists
          if (response.data.facturacion) {
            const facturacion = response.data.facturacion;

            // Separar código de país y número si viene en formato completo
            let billingPhone = facturacion.telefono || '';
            if (billingPhone && billingPhone.startsWith('+')) {
              const parsed = parsePhoneNumber(billingPhone);
              if (parsed) {
                this.selectedCountryBilling = parsed.countryCode;
                billingPhone = parsed.phoneNumber;
                console.log('📞 Teléfono de facturación parseado:', { country: parsed.countryCode, phone: parsed.phoneNumber });
              }
            }

            this.billingInfo = {
              nombreCompleto: facturacion.nombre,
              tipo_identificacion: {
                codigo: facturacion.tipo_identificacion.codigo,
                descripcion: facturacion.tipo_identificacion.descripcion
              },
              direccion: facturacion.direccion,
              telefono: billingPhone,
              email: facturacion.email,
              identificacion: facturacion.identificacion
            };
            this.isBillingDataLoaded = true;
            console.log('✅ Datos de facturación pre-cargados');
          } else {
            // Si no hay datos de facturación guardados, copiar datos de contacto
            console.log('ℹ️ No hay datos de facturación guardados, copiando datos de contacto');
            this.onContactInfoChange();
          }

          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        console.log('ℹ️ No hay sesión activa o expiró:', error.status);
        // Usuario no autenticado o sesión expirada - continuar como invitado
        // No bloquear el checkout, el usuario puede proceder sin autenticación
      }
    });
  }

  loadCartItems() {
    const sessionId = this.guestSession.getSessionId();

    console.log('🛒 Checkout - SessionID:', sessionId || '(cookie mode)');
    console.log('🔐 Session activa:', this.guestSession.isSessionActive());

    // Verificar que haya sesión activa (funciona en modo cookie y manual)
    if (!this.guestSession.isSessionActive()) {
      console.error('❌ No hay sesión activa, redirigiendo a home');
      alert('No hay sesión activa. Redirigiendo a la página principal.');
      this.router.navigate(['/']);
      return;
    }

    this.isLoadingCart = true;
    console.log('📡 Llamando a getCartBySession...');

    this.cartService.getCartBySession(sessionId).subscribe({
      next: (response) => {
        console.log('✅ Respuesta completa del carrito:', JSON.stringify(response, null, 2));

        if (response && response.success && response.data) {
          this.cartData = response.data; // Guardar respuesta completa
          this.cartItems = response.data.ticketsDetailed || [];
          this.eventInfo = response.data.evento || null;
          this.cartId = response.data.cartid;
          this.expiresAt = response.data.expiresAt || '';
          this.totalAmount = this.calculateTotal();

          // Si el carrito ya está en estado "pagando", redirigir a la página de espera
          if (response.data.estado === 'pagando') {
            console.log('⏳ Carrito en estado pagando, redirigiendo a página de espera');
            this.router.navigate(['/evento', this.urlSlug, 'checkout-payment-waiting'], {
              queryParams: { expiresAt: this.expiresAt }
            });
            return;
          }

          // Validar que el evento del carrito coincida con la URL
          if (this.eventInfo && this.urlSlug && this.eventInfo.url_slug !== this.urlSlug) {
            console.error('❌ El evento del carrito no coincide con la URL');
            alert('El carrito pertenece a otro evento. Redirigiendo al evento correcto.');
            this.router.navigate(['/evento', this.eventInfo.url_slug, 'checkout']);
            return;
          }

          console.log('📦 Cart Items length:', this.cartItems.length);
          console.log('📦 Cart Items:', this.cartItems);
          console.log('🎫 Event Info:', this.eventInfo);
          console.log('💰 Total Amount:', this.totalAmount);
          console.log('🆔 Cart ID:', this.cartId);
          console.log('⏰ Expires At:', this.expiresAt);

          // Iniciar countdown
          if (this.expiresAt) {
            this.startCountdown(this.expiresAt);
          }

          this.isLoadingCart = false;
          console.log('🏁 isLoadingCart = false - Forzando detección de cambios');

          // Agrupar tickets y calcular totales
          this.groupTicketsByType();
          this.calculateOrderTotals();

          // Cargar documentos legales del evento
          if (this.eventInfo?.eventoid) {
            this.loadLegalDocs(this.eventInfo.eventoid);
            this.loadEventoForms(this.eventInfo.eventoid);
          }

          this.cdr.detectChanges(); // Forzar detección de cambios
          console.log('📦 Cart Items:', this.cartItems);
          console.log('🎫 Event Info:', this.eventInfo);
          console.log('💰 Total Amount:', this.totalAmount);
          console.log('🆔 Cart ID:', this.cartId);

          // Temporalmente comentado para debug
          // if (this.cartItems.length === 0) {
          //   console.warn('⚠️ Carrito vacío, redirigiendo a home');
          //   alert('El carrito está vacío. Redirigiendo a la página principal.');
          //   setTimeout(() => {
          //     this.router.navigate(['/']);
          //   }, 100);
          // }
        } else {
          console.error('❌ Respuesta sin datos o no exitosa:', response);
          // Temporalmente comentado para debug
          // alert('No se pudo cargar el carrito. Redirigiendo a la página principal.');
          // setTimeout(() => {
          //   this.router.navigate(['/']);
          // }, 100);
          this.isLoadingCart = false;
          this.cdr.detectChanges();
        }
        console.log('🟢 Finalizando carga - isLoadingCart:', this.isLoadingCart);
      },
      error: (error) => {
        console.error('❌ Error al cargar el carrito:', error);
        console.error('Error completo:', JSON.stringify(error, null, 2));
        // Temporalmente comentado para debug
        // alert('Error al cargar el carrito: ' + (error.message || 'Error desconocido'));
        this.isLoadingCart = false;
        this.cdr.detectChanges();
        // setTimeout(() => {
        //   this.router.navigate(['/']);
        // }, 100);
      }
    });
  }

  calculateTotal(): number {
    return this.cartItems.reduce((sum, item) => sum + (item.precio_pagado || 0), 0);
  }

  openChangePriceModal(ticket: TicketDetailed): void {
    this.ticketForPriceChange = ticket;
    this.selectedNewPrice = null;
    this.availablePricesForChange = [];
    this.showChangePriceModal = true;
    this.isLoadingPricesForChange = true;

    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.tiposTicket.getByIdForWeb.replace('{tipoticketid}', ticket.tipoticketid)}`;
    this.http.get<any>(url, {
      headers: { 'x-api-key': API_CONFIG.api_key },
      withCredentials: API_CONFIG.cookie_http_only
    }).subscribe({
      next: (res) => {
        this.availablePricesForChange = Array.isArray(res?.data?.precios)
          ? res.data.precios.map((precio: any) => ({
            ...precio,
            id: precio?.id || precio?.precioid,
            precioid: precio?.precioid || precio?.id,
          }))
          : [];
        this.isLoadingPricesForChange = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoadingPricesForChange = false;
        this.cdr.detectChanges();
      }
    });
  }

  closeChangePriceModal(): void {
    this.showChangePriceModal = false;
    this.ticketForPriceChange = null;
    this.selectedNewPrice = null;
    this.availablePricesForChange = [];
  }

  async confirmPriceChange(): Promise<void> {
    if (!this.ticketForPriceChange || !this.selectedNewPrice || !this.cartId) return;

    // No cambiar si es el mismo precio
    if (this.getPrecioId(this.selectedNewPrice) === this.getPrecioId(this.ticketForPriceChange.precioTipoTicket)) {
      this.closeChangePriceModal();
      return;
    }

    this.isChangingPrice = true;
    const ticket = this.ticketForPriceChange;
    const newPrecioId = this.getPrecioId(this.selectedNewPrice);
    const sessionId = this.guestSession.getSessionId();

    try {
      // Cambiar precio in-place (no reinicia el timer del carrito)
      await this.cartService.changeTicketPrecio({
        sessionId,
        tipoticketid: ticket.tipoticketid,
        ticketid: ticket.ticketid,
        precioId: newPrecioId
      }).toPromise();

      this.closeChangePriceModal();
      // Recargar carrito
      this.loadCartItems();
    } catch (error) {
      console.error('❌ Error al cambiar precio:', error);
    } finally {
      this.isChangingPrice = false;
      this.cdr.detectChanges();
    }
  }

  getPrecioId(precio: { id?: string; precioid?: string } | null | undefined): string {
    return precio?.precioid || precio?.id || '';
  }

  groupTicketsByType(): void {
    const grouped = new Map<string, TicketGroup>();




    this.cartItems.forEach(ticket => {
      const tipoticketid = ticket.tipoticketid;
      const precioInfo = ticket.precioTipoTicket;

      if (!precioInfo) return;

      const groupKey = `${tipoticketid}__${precioInfo.id || precioInfo.nombre_precio}`;

      if (!grouped.has(groupKey)) {
        // Crear nuevo grupo
        grouped.set(groupKey, {
          tipoticketid,
          nombreLocalidad: ticket.tipoticket?.nombre_localidad || ticket.nombre_localidad || '',
          nombrePrecio: precioInfo.nombre_precio || 'Sin nombre',
          cantidad: 1,
          precioBase: precioInfo.precio_base || 0,
          ivaTicket: precioInfo.iva_ticket || 0,
          precioTicketFinal: precioInfo.precio_ticket_final || 0,
          cargoServicio: precioInfo.precio_cargo_servicio || 0,
          ivaCargoServicio: precioInfo.iva_cargo_servicio || 0,
          cargoServicioFinal: precioInfo.precio_cargo_servicio_final || 0,
          servicioAdministrativo: precioInfo.precio_servicio_administrativo || 0,
          ivaServicioAdministrativo: precioInfo.iva_servicio_administrativo || 0,
          servicioAdministrativoFinal: precioInfo.precio_servicio_administrativo_final || 0,
          precioTotal: precioInfo.precio_total || 0,
          subtotal: precioInfo.precio_total || 0,
          nombreMoneda: precioInfo.nombre_moneda || 'USD',
          nombreSubevento: ticket.tipoticket?.subevento?.nombre || '',
          funcionFecha: ticket.tipoticket?.funcion?.fecha || '',
          funcionHora: ticket.tipoticket?.funcion?.hora || '',
          tipoprecio: precioInfo.tipoprecio || ''
        });
      } else {
        // Incrementar cantidad y subtotal
        const group = grouped.get(groupKey)!;
        group.cantidad++;
        group.subtotal = group.cantidad * group.precioTotal;
      }



    });

    this.ticketGroups = Array.from(grouped.values());
    console.log('✅ Tickets agrupados:', this.ticketGroups);
  }

  calculateOrderTotals(): void {
    let subtotalBase = 0;
    let totalIvaTickets = 0;
    let totalCargoServicio = 0;
    let totalIvaCargoServicio = 0;
    let totalServicioAdministrativo = 0;
    let totalIvaServicioAdministrativo = 0;
    let totalGeneral = 0;
    let nombreMoneda = 'USD';

    this.ticketGroups.forEach(group => {
      const cantidad = group.cantidad;

      // Calcular totales por componente
      subtotalBase += group.precioBase * cantidad;
      totalIvaTickets += (group.precioTicketFinal - group.precioBase) * cantidad;
      totalCargoServicio += group.cargoServicio * cantidad;
      totalIvaCargoServicio += (group.cargoServicioFinal - group.cargoServicio) * cantidad;
      totalServicioAdministrativo += group.servicioAdministrativo * cantidad;
      totalIvaServicioAdministrativo += (group.servicioAdministrativoFinal - group.servicioAdministrativo) * cantidad;
      totalGeneral += group.subtotal;

      nombreMoneda = group.nombreMoneda;
    });

    // Redondear todos los totales a 2 decimales
    this.orderTotals = {
      subtotalBase: Math.round(subtotalBase * 100) / 100,
      totalIvaTickets: Math.round(totalIvaTickets * 100) / 100,
      totalCargoServicio: Math.round(totalCargoServicio * 100) / 100,
      totalIvaCargoServicio: Math.round(totalIvaCargoServicio * 100) / 100,
      totalServicioAdministrativo: Math.round(totalServicioAdministrativo * 100) / 100,
      totalIvaServicioAdministrativo: Math.round(totalIvaServicioAdministrativo * 100) / 100,
      totalGeneral: Math.round(totalGeneral * 100) / 100,
      nombreMoneda
    };

    console.log('✅ Totales calculados (redondeados a 2 decimales):', this.orderTotals);
  }

  loadTiposIdentificacion() {
    this.isLoadingTiposId = true;
    fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.tiposIdentificacion.list}`, {
      headers: {
        'x-api-key': API_CONFIG.api_key
      }
    })
      .then(response => response.json())
      .then((data: TipoIdentificacionResponse) => {
        if (data.success && data.data) {
          // Filtrar tipos activos y excluir códigos 07 y 08
          this.tiposIdentificacion = data.data.filter(
            tipo => tipo.activo && tipo.codigo !== '07' && tipo.codigo !== '08'
          );
          console.log('✅ Tipos de identificación cargados:', this.tiposIdentificacion);
        }
        this.isLoadingTiposId = false;
        this.cdr.detectChanges();
      })
      .catch(error => {
        console.error('❌ Error al cargar tipos de identificación:', error);
        this.isLoadingTiposId = false;
        this.cdr.detectChanges();
      });
  }

  loadLegalDocs(eventoid: string): void {
    this.isLoadingLegalDocs = true;
    const endpoint = API_CONFIG.endpoints.eventos.getLegalDocs.replace('{eventoid}', eventoid);

    this.http.get<LegalDocsResponse>(
      `${API_CONFIG.baseUrl}${endpoint}`,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key
        }
      }
    ).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.legalDocs = response.data;
          console.log('✅ Documentos legales cargados:', this.legalDocs);
        } else {
          console.warn('⚠️ No se encontraron documentos legales para el evento');
        }
        this.isLoadingLegalDocs = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error al cargar documentos legales:', error);
        this.isLoadingLegalDocs = false;
        this.cdr.detectChanges();
      }
    });
  }

  loadEventoForms(eventoid: string): void {
    this.isLoadingForms = true;
    const endpoint = API_CONFIG.endpoints.eventos.getEventoForms.replace('{eventoid}', eventoid);

    this.http.get<EventoFormResponse>(
      `${API_CONFIG.baseUrl}${endpoint}`,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key
        }
      }
    ).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.eventoForms = response.data;
          console.log('✅ Formularios del evento cargados:', this.eventoForms);
          // Inicializar datos de formulario para cada ticket
          this.initializeTicketForms();
        } else {
          console.warn('⚠️ No se encontraron formularios para el evento');
        }
        this.isLoadingForms = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error al cargar formularios del evento:', error);
        this.isLoadingForms = false;
        this.cdr.detectChanges();
      }
    });
  }

  initializeTicketForms(): void {
    // Inicializar datos de formulario para cada ticket en el carrito
    this.cartItems.forEach(ticket => {
      const existing = this.ticketFormsData.find(t => t.ticketid === ticket.ticketid);
      if (!existing) {
        const ticketForm: TicketFormData = {
          ticketid: ticket.ticketid,
          forms: this.eventoForms.map(eventoForm => {
            const formData: { [key: string]: any } = {};
            eventoForm.templateForm.formfields.forEach(field => {
              formData[field.name] = field.defaultValue || '';
            });
            return {
              templateformid: eventoForm.templateformid,
              formData
            };
          })
        };
        this.ticketFormsData.push(ticketForm);
      }
    });
    // Limpiar tickets que ya no están en el carrito
    this.ticketFormsData = this.ticketFormsData.filter(
      tf => this.cartItems.some(ci => ci.ticketid === tf.ticketid)
    );
    console.log('✅ Datos de formularios inicializados para', this.ticketFormsData.length, 'tickets');
  }

  getFieldValue(ticketid: string, templateformid: string, fieldName: string): any {
    const ticketForm = this.ticketFormsData.find(t => t.ticketid === ticketid);
    if (!ticketForm) return '';
    const form = ticketForm.forms.find(f => f.templateformid === templateformid);
    if (!form) return '';
    return form.formData[fieldName] ?? '';
  }

  private getTicketFormData(ticketid: string, templateformid: string): { [key: string]: any } | null {
    const ticketForm = this.ticketFormsData.find(t => t.ticketid === ticketid);
    if (!ticketForm) return null;
    const form = ticketForm.forms.find(f => f.templateformid === templateformid);
    if (!form) return null;
    return form.formData;
  }

  private getEventoFormByTemplate(templateformid: string): EventoForm | undefined {
    return this.eventoForms.find(ef => ef.templateformid === templateformid);
  }

  private getPatternFromRegexType(regexType?: string): string | undefined {
    if (regexType === 'numbers') {
      return '^\\d+$';
    }
    if (regexType === 'letters') {
      return '^[A-Za-zÁÉÍÓÚáéíóúÑñ]+$';
    }
    if (regexType === 'alphanumeric') {
      return '^[A-Za-zÁÉÍÓÚáéíóúÑñ0-9]+$';
    }
    return undefined;
  }

  private getActiveConditionalRule(
    ticketid: string,
    templateformid: string,
    targetFieldName: string
  ): ConditionalValidationRule | null {
    const eventoForm = this.getEventoFormByTemplate(templateformid);
    const rules = eventoForm?.templateForm?.conditionalValidations || [];
    if (!rules.length) {
      return null;
    }

    const formData = this.getTicketFormData(ticketid, templateformid) || {};

    for (const rule of rules) {
      if (rule.active === false) {
        continue;
      }
      if (rule.target?.field !== targetFieldName) {
        continue;
      }

      const sourceValue = formData[rule.when?.field];
      const normalizedSource = sourceValue === undefined || sourceValue === null ? '' : String(sourceValue).trim();
      const normalizedExpected = String(rule.when?.value || '').trim();
      if (normalizedSource === normalizedExpected) {
        return rule;
      }
    }

    return null;
  }

  private getEffectiveFieldConstraints(
    ticketid: string,
    templateformid: string,
    field: FormField
  ): EffectiveFieldConstraints {
    const base: EffectiveFieldConstraints = {
      min: field.min,
      max: field.max,
      minLength: field.validation?.minLength,
      maxLength: field.maxlength ?? field.validation?.maxLength,
      pattern: field.validation?.pattern,
      onlyNumbers: false
    };

    const rule = this.getActiveConditionalRule(ticketid, templateformid, field.name);
    if (!rule) {
      return base;
    }

    const enforcement = rule.enforcement || {};
    const patternByType = this.getPatternFromRegexType(enforcement.regexType);

    return {
      min: enforcement.min !== undefined ? enforcement.min : base.min,
      max: enforcement.max !== undefined ? enforcement.max : base.max,
      minLength: enforcement.minLength !== undefined ? enforcement.minLength : base.minLength,
      maxLength: enforcement.maxLength !== undefined ? enforcement.maxLength : base.maxLength,
      pattern: enforcement.pattern || patternByType || base.pattern,
      onlyNumbers: !!enforcement.onlyNumbers || enforcement.regexType === 'numbers',
      message: enforcement.message
    };
  }

  updateTicketFormField(ticketid: string, templateformid: string, fieldName: string, value: any): void {
    let ticketForm = this.ticketFormsData.find(t => t.ticketid === ticketid);
    if (!ticketForm) {
      ticketForm = { ticketid, forms: [] };
      this.ticketFormsData.push(ticketForm);
    }
    let form = ticketForm.forms.find(f => f.templateformid === templateformid);
    if (!form) {
      form = { templateformid, formData: {} };
      ticketForm.forms.push(form);
    }
    form.formData[fieldName] = value;
    this.syncTicketFormsToFacturacion();
  }

  isTicketFormValid(ticketid: string): boolean {
    const ticketForm = this.ticketFormsData.find(t => t.ticketid === ticketid);
    if (!ticketForm) return false;

    // Validar que todos los campos requeridos estén llenos y que las restricciones se cumplan
    for (const eventoForm of this.eventoForms) {
      const form = ticketForm.forms.find(f => f.templateformid === eventoForm.templateformid);
      for (const field of eventoForm.templateForm.formfields) {
        const value = form?.formData[field.name];
        const constraints = this.getEffectiveFieldConstraints(ticketid, eventoForm.templateformid, field);
        if (field.required) {
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            return false;
          }
        }

        if (value !== undefined && value !== null && value !== '') {
          const rawValue = String(value);

          if (constraints.onlyNumbers && !/^\d+$/.test(rawValue)) {
            return false;
          }

          if (constraints.pattern) {
            try {
              const rx = new RegExp(constraints.pattern);
              if (!rx.test(rawValue)) {
                return false;
              }
            } catch {
              // Si el regex está mal configurado, no bloquear el checkout.
            }
          }

          if (constraints.minLength !== undefined && rawValue.length < constraints.minLength) {
            return false;
          }

          if (constraints.maxLength !== undefined && rawValue.length > constraints.maxLength) {
            return false;
          }
        }

        // Validar restricciones de campos numéricos
        if (field.type === 'number' && value !== undefined && value !== null && value !== '') {
          const numValue = Number(value);
          if (isNaN(numValue)) return false;
          if (constraints.min !== undefined && numValue < constraints.min) return false;
          if (constraints.max !== undefined && numValue > constraints.max) return false;
          const maxLen = constraints.maxLength;
          if (maxLen !== undefined && String(value).replace(/[^0-9]/g, '').length > maxLen) return false;
        }
      }
    }
    return true;
  }

  /**
   * Obtener mensaje de error de validación para un campo numérico
   */
  getFieldError(ticketid: string, templateformid: string, field: FormField): string | null {
    const value = this.getFieldValue(ticketid, templateformid, field.name);
    if (value === undefined || value === null || value === '') return null;

    const constraints = this.getEffectiveFieldConstraints(ticketid, templateformid, field);
    const stringValue = String(value);

    if (constraints.onlyNumbers && !/^\d+$/.test(stringValue)) {
      return constraints.message || 'Solo se permiten números.';
    }

    if (constraints.pattern) {
      try {
        const rx = new RegExp(constraints.pattern);
        if (!rx.test(stringValue)) {
          return constraints.message || 'Formato inválido.';
        }
      } catch {
        // Ignorar regex inválido configurado desde backend/admin.
      }
    }

    if (constraints.minLength !== undefined && stringValue.length < constraints.minLength) {
      return constraints.message || `Mínimo ${constraints.minLength} caracteres.`;
    }

    if (constraints.maxLength !== undefined && stringValue.length > constraints.maxLength) {
      return constraints.message || `Máximo ${constraints.maxLength} caracteres.`;
    }

    if (field.type !== 'number') return null;

    const numValue = Number(value);
    if (isNaN(numValue)) return 'Ingrese un número válido.';
    if (constraints.min !== undefined && numValue < constraints.min) {
      return constraints.message || `El valor mínimo es ${constraints.min}.`;
    }
    if (constraints.max !== undefined && numValue > constraints.max) {
      return constraints.message || `El valor máximo es ${constraints.max}.`;
    }
    const maxLen = constraints.maxLength;
    if (maxLen !== undefined && String(value).replace(/[^0-9]/g, '').length > maxLen) {
      return constraints.message || `Máximo ${maxLen} dígitos.`;
    }
    return null;
  }

  /**
   * Limitar la cantidad de dígitos en un campo numérico según maxlength
   */
  enforceNumberMaxLength(event: Event, field: FormField, ticketid: string, templateformid: string): void {
    const input = event.target as HTMLInputElement;
    const constraints = this.getEffectiveFieldConstraints(ticketid, templateformid, field);
    const maxLen = constraints.maxLength;
    if (maxLen !== undefined && input.value.length > maxLen) {
      input.value = input.value.slice(0, maxLen);
      input.dispatchEvent(new Event('input'));
    }
  }

  /**
   * Sincronizar ticketFormsData al servicio de facturación
   */
  private syncTicketFormsToFacturacion(): void {
    const data = this.facturacionService.getFacturacionData();
    if (data) {
      data.ticketformsdata = [...this.ticketFormsData];
      this.facturacionService.setFacturacionData(data);
    }
  }

  /**
   * Eliminar datos de formulario de un ticket específico
   */
  removeTicketFormData(ticketid: string): void {
    this.ticketFormsData = this.ticketFormsData.filter(t => t.ticketid !== ticketid);
    this.syncTicketFormsToFacturacion();
  }

  /**
   * Obtener maxlength para un campo de formulario
   */
  getFieldMaxLength(field: FormField, ticketid: string, templateformid: string): number {
    const constraints = this.getEffectiveFieldConstraints(ticketid, templateformid, field);
    if (constraints.maxLength !== undefined && constraints.maxLength !== null) {
      return constraints.maxLength;
    }
    // Valores por defecto según el tipo
    if (field.type === 'textarea') {
      return 500;
    }
    return 50; // Default para text, email, tel
  }

  getFieldMin(field: FormField, ticketid: string, templateformid: string): number | null {
    const constraints = this.getEffectiveFieldConstraints(ticketid, templateformid, field);
    return constraints.min !== undefined ? constraints.min : null;
  }

  getFieldMax(field: FormField, ticketid: string, templateformid: string): number | null {
    const constraints = this.getEffectiveFieldConstraints(ticketid, templateformid, field);
    return constraints.max !== undefined ? constraints.max : null;
  }

  getFieldMinLengthHint(field: FormField, ticketid: string, templateformid: string): number | null {
    const constraints = this.getEffectiveFieldConstraints(ticketid, templateformid, field);
    return constraints.minLength !== undefined ? constraints.minLength : null;
  }

  getFieldMaxLengthHint(field: FormField, ticketid: string, templateformid: string): number | null {
    const constraints = this.getEffectiveFieldConstraints(ticketid, templateformid, field);
    return constraints.maxLength !== undefined ? constraints.maxLength : null;
  }

  getFieldExactLengthHint(field: FormField, ticketid: string, templateformid: string): number | null {
    const constraints = this.getEffectiveFieldConstraints(ticketid, templateformid, field);
    if (
      constraints.minLength !== undefined &&
      constraints.maxLength !== undefined &&
      constraints.minLength === constraints.maxLength
    ) {
      return constraints.minLength;
    }
    return null;
  }

  hasFieldConstraintHints(field: FormField, ticketid: string, templateformid: string): boolean {
    const constraints = this.getEffectiveFieldConstraints(ticketid, templateformid, field);
    return (
      constraints.min !== undefined ||
      constraints.max !== undefined ||
      constraints.minLength !== undefined ||
      constraints.maxLength !== undefined
    );
  }

  areAllTicketFormsValid(): boolean {
    if (this.eventoForms.length === 0) return true; // No hay formularios, es válido

    for (const ticket of this.cartItems) {
      if (!this.isTicketFormValid(ticket.ticketid)) {
        return false;
      }
    }
    return true;
  }

  toggleTerminos(): void {
    this.showTerminos = !this.showTerminos;
  }

  togglePoliticaDatos(): void {
    this.showPoliticaDatos = !this.showPoliticaDatos;
  }

  canProceedToPayment(): boolean {
    if (!this.isValidEmail(this.userInfo.email)) {
      return false;
    }
    if (!this.isValidEmail(this.billingInfo.email)) {
      return false;
    }
    // Validar que los emails coincidan si no está autenticado
    if (!this.isProfileDataLoaded) {
      if (this.userInfo.email !== this.emailConfirmation) {
        return false;
      }
    }
    // Validar que todos los formularios de tickets estén completos
    if (!this.areAllTicketFormsValid()) {
      return false;
    }
    return this.aceptaTerminos && this.aceptaPoliticaDatos;
  }

  isValidEmail(email: string): boolean {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  emailsMatch(): boolean {
    if (!this.userInfo.email || !this.emailConfirmation) return true;
    return this.userInfo.email === this.emailConfirmation;
  }

  onTipoIdentificacionChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    const codigo = select.value;
    const tipo = this.tiposIdentificacion.find(t => t.codigo === codigo);
    if (tipo) {
      this.billingInfo.tipo_identificacion = {
        codigo: tipo.codigo,
        descripcion: tipo.descripcion
      };
      console.log('Tipo de identificación seleccionado:', this.billingInfo.tipo_identificacion);
    }
  }

  getTipoIdentificacionValidacion(): string {
    const tipo = this.tiposIdentificacion.find(
      t => t.codigo === this.billingInfo.tipo_identificacion.codigo
    );
    return tipo?.validacion || 'Ingrese el número de identificación';
  }

  getMaxLengthIdentificacion(): number {
    const codigo = this.billingInfo.tipo_identificacion.codigo;
    if (codigo === '04') return 13; // RUC
    if (codigo === '05') return 10; // CEDULA
    return 30; // Otros
  }

  validateIdentificacion(): string | null {
    const codigo = this.billingInfo.tipo_identificacion.codigo;
    const identificacion = this.billingInfo.identificacion;

    if (!identificacion) return null;

    // RUC (04): debe tener 13 dígitos y terminar en 001
    if (codigo === '04') {
      if (!/^\d{13}$/.test(identificacion)) {
        return 'El RUC debe tener exactamente 13 dígitos';
      }
      if (!identificacion.endsWith('001')) {
        return 'El RUC debe terminar en 001';
      }
    }

    // CEDULA (05): debe tener 10 dígitos
    if (codigo === '05') {
      if (!/^\d{10}$/.test(identificacion)) {
        return 'La cédula debe tener exactamente 10 dígitos';
      }
    }

    return null;
  }

  // Convertir a mayúsculas los campos de texto
  toUpperCase(field: 'firstname' | 'lastname' | 'nombreCompleto') {
    if (field === 'firstname') {
      this.userInfo.firstname = this.userInfo.firstname.toUpperCase();
    } else if (field === 'lastname') {
      this.userInfo.lastname = this.userInfo.lastname.toUpperCase();
    } else if (field === 'nombreCompleto') {
      this.billingInfo.nombreCompleto = this.billingInfo.nombreCompleto.toUpperCase();
    }
  }

  // Métodos para manejo de selectores de país

  getSelectedCountry(countryCode: string): Country | undefined {
    return this.countries.find(c => c.code === countryCode);
  }

  selectCountry(country: Country, field: 'contact' | 'billing') {
    if (field === 'contact') {
      this.selectedCountryContact = country.code;
      this.showCountryDropdownContact = false;
      // Sincronizar con facturación
      this.selectedCountryBilling = country.code;
    } else {
      this.selectedCountryBilling = country.code;
      this.showCountryDropdownBilling = false;
    }
  }

  toggleCountryDropdown(field: 'contact' | 'billing') {
    if (field === 'contact') {
      this.showCountryDropdownContact = !this.showCountryDropdownContact;
      if (this.showCountryDropdownContact) {
        console.log('📋 Países disponibles:', this.countries.length, this.countries.map(c => c.name));
      }
    } else {
      this.showCountryDropdownBilling = !this.showCountryDropdownBilling;
      if (this.showCountryDropdownBilling) {
        console.log('📋 Países disponibles:', this.countries.length, this.countries.map(c => c.name));
      }
    }
  }

  validatePhone(field: 'contact' | 'billing'): string | null {
    const phone = field === 'contact' ? this.userInfo.phone : this.billingInfo.telefono;
    const countryCode = field === 'contact' ? this.selectedCountryContact : this.selectedCountryBilling;

    if (!phone) return null;

    const cleanPhone = phone.replace(/\D/g, '');
    if (!cleanPhone) return null;

    if (!validatePhoneNumber(cleanPhone, countryCode)) {
      return getPhoneValidationMessage(countryCode);
    }

    return null;
  }

  getPhonePlaceholder(field: 'contact' | 'billing'): string {
    const countryCode = field === 'contact' ? this.selectedCountryContact : this.selectedCountryBilling;
    const country = this.getSelectedCountry(countryCode);
    return country?.placeholder || 'Ingrese su teléfono';
  }

  getPhoneMaxLength(field: 'contact' | 'billing'): number {
    const countryCode = field === 'contact' ? this.selectedCountryContact : this.selectedCountryBilling;
    const country = this.getSelectedCountry(countryCode);
    return country?.maxDigits || 15;
  }

  onPhoneInput(field: 'contact' | 'billing') {
    // Eliminar caracteres no numéricos
    const maxLength = this.getPhoneMaxLength(field);

    if (field === 'contact') {
      this.userInfo.phone = this.userInfo.phone.replace(/\D/g, '');
      // Limitar al máximo de dígitos permitidos
      if (this.userInfo.phone.length > maxLength) {
        this.userInfo.phone = this.userInfo.phone.substring(0, maxLength);
      }
      // Sincronizar con facturación
      this.billingInfo.telefono = this.userInfo.phone;
    } else {
      this.billingInfo.telefono = this.billingInfo.telefono.replace(/\D/g, '');
      // Limitar al máximo de dígitos permitidos
      if (this.billingInfo.telefono.length > maxLength) {
        this.billingInfo.telefono = this.billingInfo.telefono.substring(0, maxLength);
      }
    }
  }

  // Método para obtener el teléfono completo con código de país
  getFullPhoneNumber(field: 'contact' | 'billing'): string {
    const phone = field === 'contact' ? this.userInfo.phone : this.billingInfo.telefono;
    const countryCode = field === 'contact' ? this.selectedCountryContact : this.selectedCountryBilling;
    return formatPhoneNumber(phone, countryCode);
  }

  // Calcular el monto de la comisión basado en el total
  calculateComisionAmount(comisionPorcentaje: number): number {
    if (!this.orderTotals || !this.orderTotals.totalGeneral) {
      return 0;
    }
    return (this.orderTotals.totalGeneral * comisionPorcentaje) / 100;
  }

  // Calcular el total con comisión incluida
  calculateTotalConComision(comisionPorcentaje: number): number {
    if (!this.orderTotals || !this.orderTotals.totalGeneral) {
      return 0;
    }
    return this.orderTotals.totalGeneral + this.calculateComisionAmount(comisionPorcentaje);
  }

  // Método para sincronizar datos de contacto a facturación
  onContactInfoChange() {
    // Cuando cambien nombre o apellido, actualizar nombre completo de facturación
    if (this.userInfo.firstname || this.userInfo.lastname) {
      this.billingInfo.nombreCompleto = `${this.userInfo.firstname} ${this.userInfo.lastname}`.trim();
    }
    // Sincronizar email y teléfono
    if (this.userInfo.email) {
      this.billingInfo.email = this.userInfo.email;
    }
    if (this.userInfo.phone) {
      this.billingInfo.telefono = this.userInfo.phone;
    }
    // Sincronizar país del teléfono
    this.selectedCountryBilling = this.selectedCountryContact;
  }

  continueCheckout() {
    // Validate contact info
    if (!this.userInfo.firstname || !this.userInfo.lastname ||
      !this.userInfo.email || !this.userInfo.phone) {
      alert('Por favor, complete todos los campos de contacto');
      return;
    }

    // Validate contact email format
    if (!this.isValidEmail(this.userInfo.email)) {
      alert('El correo electrónico de contacto no es válido');
      return;
    }

    // Validate contact phone
    const phoneContactError = this.validatePhone('contact');
    if (phoneContactError) {
      alert(`Error en teléfono de contacto: ${phoneContactError}`);
      return;
    }

    // Validate billing info
    if (!this.billingInfo.nombreCompleto || !this.billingInfo.tipo_identificacion.codigo ||
      !this.billingInfo.identificacion || !this.billingInfo.direccion ||
      !this.billingInfo.telefono || !this.billingInfo.email) {
      alert('Por favor, complete todos los campos de facturación');
      return;
    }

    // Validate billing email format
    if (!this.isValidEmail(this.billingInfo.email)) {
      alert('El correo electrónico de facturación no es válido');
      return;
    }

    // Validate billing phone
    const phoneBillingError = this.validatePhone('billing');
    if (phoneBillingError) {
      alert(`Error en teléfono de facturación: ${phoneBillingError}`);
      return;
    }

    // Validate identificación format
    const identificacionError = this.validateIdentificacion();
    if (identificacionError) {
      alert(identificacionError);
      return;
    }

    // Cargar métodos de pago
    this.loadMetodosPago();
  }

  loadMetodosPago() {
    if (!this.eventInfo?.eventoid) {
      console.error('❌ No hay eventoid disponible');
      alert('Error: No se pudo obtener la información del evento');
      return;
    }

    // Validar que se hayan aceptado los documentos legales
    if (!this.canProceedToPayment()) {
      alert('Debe aceptar los Términos y Condiciones y la Política de Datos para continuar');
      return;
    }

    console.log('🔄 Iniciando carga de métodos de pago...');
    console.log('📊 Order Totals:', this.orderTotals);
    console.log('🎯 selectedMetodoPago actual:', this.selectedMetodoPago);
    console.log('🎭 showPaymentModal antes:', this.showPaymentModal);

    // Limpiar selección previa y comisiones
    this.selectedMetodoPago = null;
    this.comisionMetodoPago = 0;
    this.porcentajeComision = 0;
    this.totalConComision = 0;
    this.rendered = false;

    console.log('🧹 Limpiado selectedMetodoPago y comisiones');

    this.isLoadingMetodosPago = true;
    const eventoid = this.eventInfo.eventoid;
    const endpoint = API_CONFIG.endpoints.eventos.getMetodosPago.replace('{eventoid}', eventoid);

    fetch(`${API_CONFIG.baseUrl}${endpoint}`, {
      headers: {
        'x-api-key': API_CONFIG.api_key
      }
    })
      .then(response => response.json())
      .then((data: MetodoPagoResponse) => {
        console.log('📦 Respuesta de métodos de pago:', data);
        if (data.success && data.data) {
          const activos = data.data.filter(metodo => {
            const isActive = metodo.estado === 'activa';
            const hasWebAvailable = (metodo.canales || []).some(
              canal => canal.canal?.toLowerCase() === 'web' && canal.disponible === true
            );
            return isActive && hasWebAvailable;
          });

          const hasPayphone = activos.some(m => m.nombre?.toUpperCase() === 'PAYPHONE');
          this.metodosPago = hasPayphone
            ? activos.filter(m => m.nombre?.toUpperCase() === 'PAYPHONE')
            : activos;

          console.log('✅ Métodos de pago cargados:', this.metodosPago);
          console.log('📝 metodosPago.length:', this.metodosPago.length);

          // Mostrar modal con métodos de pago
          this.showPaymentModal = true;
          console.log('🎭 Modal de pago mostrado (después):', this.showPaymentModal);
          console.log('🔍 Forzando detección de cambios...');
          this.cdr.detectChanges();
          console.log('✅ Detección de cambios completada');
        } else {
          console.error('❌ No se encontraron métodos de pago');
          alert('No hay métodos de pago disponibles para este evento');
        }
        this.isLoadingMetodosPago = false;
        this.cdr.detectChanges();
      })
      .catch(error => {
        console.error('❌ Error al cargar métodos de pago:', error);
        alert('Error al cargar los métodos de pago. Intente nuevamente.');
        this.isLoadingMetodosPago = false;
        this.cdr.detectChanges();
      });
  }

  closePaymentModal() {
    this.showPaymentModal = false;
  }

  generatePaymentId(): Promise<string> {
    return new Promise((resolve, reject) => {
      const requestBody = {
        cartid: this.cartId,
        contacto: {
          email: this.userInfo.email,
          nombre: `${this.userInfo.firstname} ${this.userInfo.lastname}`,
          telefono: this.userInfo.phone
        },
        informacionfactura: {
          nombre: this.billingInfo.nombreCompleto,
          tipo_identificacion: {
            codigo: this.billingInfo.tipo_identificacion.codigo,
            descripcion: this.billingInfo.tipo_identificacion.descripcion
          },
          identificacion: this.billingInfo.identificacion,
          direccion: this.billingInfo.direccion,
          telefono: this.billingInfo.telefono,
          email: this.billingInfo.email
        }
      };

      fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.carrito.generatePaymentId}`, {
        method: 'POST',
        headers: {
          'x-api-key': API_CONFIG.api_key,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })
        .then(response => response.json())
        .then(data => {
          if (data.success && data.data && data.data.paymentid) {
            console.log('✅ Payment ID generado:', data.data.paymentid);
            this.paymentId = data.data.paymentid;

            // Actualizar expiresAt si viene en la respuesta
            if (data.data.expiresAt) {
              this.expiresAt = data.data.expiresAt;
              console.log('🕐 Nuevo tiempo de expiración:', this.expiresAt);
              // Reiniciar countdown con nuevo tiempo
              this.startCountdown(this.expiresAt);
            }

            // Refrescar el carrito para obtener los datos actualizados
            console.log('🔄 Refrescando carrito después de generar payment ID...');
            this.refreshCartData();

            resolve(data.data.paymentid);
          } else {
            console.error('❌ Error en respuesta de payment ID:', data);
            reject(new Error(data.message || 'Error al generar payment ID'));
          }
        })
        .catch(error => {
          console.error('❌ Error al generar payment ID:', error);
          reject(error);
        });
    });
  }

  refreshCartData() {
    // Verificar que haya sesión activa
    if (!this.guestSession.isSessionActive()) return;

    const sessionId = this.guestSession.getSessionId();

    this.cartService.getCartBySession(sessionId).subscribe({
      next: (response) => {
        if (response && response.success && response.data) {
          this.cartData = response.data;
          this.expiresAt = response.data.expiresAt || '';
          console.log('✅ Carrito refrescado:', this.cartData);
          console.log('🕐 Nuevo expiresAt:', this.expiresAt);

          // Actualizar countdown
          if (this.expiresAt) {
            this.startCountdown(this.expiresAt);
          }

          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        console.error('❌ Error al refrescar carrito:', error);
      }
    });
  }

  selectMetodoPago(metodo: MetodoPago) {
    this.selectedMetodoPago = metodo;
    this.showPaymentModal = false;
    console.log('💳 Método de pago seleccionado:', metodo);

    const contacto = {
      userid: this.currentUserId,
      username: '',
      firstname: this.userInfo.firstname,
      lastname: this.userInfo.lastname,
      email: this.userInfo.email,
      phone: this.getFullPhoneNumber('contact')
    };

    const informacionfactura = {
      nombre: this.billingInfo.nombreCompleto,
      direccion: this.billingInfo.direccion,
      telefono: this.getFullPhoneNumber('billing'),
      email: this.billingInfo.email,
      identificacion: this.billingInfo.identificacion,
      tipo_identificacion: {
        codigo: this.billingInfo.tipo_identificacion.codigo,
        descripcion: this.billingInfo.tipo_identificacion.descripcion
      }
    };

    const finalizeFacturacionData = (effectiveCartId: string) => {
      // Compatibilidad: paymentid se mantiene, pero ahora es cartid
      this.paymentId = effectiveCartId;
      this.cartId = effectiveCartId;

      const facturacionData: FacturacionData = {
        cartid: effectiveCartId,
        paymentid: effectiveCartId,
        userid: this.currentUserId,
        eventoid: this.eventInfo?.eventoid || '',
        contacto,
        informacionfactura,
        eventometodopagoid: metodo.metodopagoid,
        pasarelaData: null,
        payphoneToken: metodo.tokenId,
        ticketformsdata: [...this.ticketFormsData]
      };

      this.facturacionService.setFacturacionData(facturacionData);
      console.log('💾 Datos de facturación guardados en el servicio');
      console.log('📋 Factura completa:', JSON.stringify(facturacionData, null, 2));

      if (metodo.nombre === 'PAYPHONE') {
        setTimeout(() => {
          this.pagarWIthPAYPHONE();
        }, 100);
      }
    };

    // Nuevo flujo para Payphone: preparar cart en backend para reuso seguro de clientTransactionId
    if (metodo.nombre === 'PAYPHONE') {
      this.cartService.preparePayphone(this.cartId, {
        contacto,
        informacionfactura,
        ticketformsdata: [...this.ticketFormsData],
        pasarela_pago: {
          eventometodopagoid: metodo.metodopagoid,
          nombre: metodo.nombre,
          comision: metodo.comision || 0,
          pasarelaData: null
        },
        audit: {
          flowSource: 'checkout-payphone',
          referer: typeof window !== 'undefined' ? window.location.href : undefined,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
        }
      }).subscribe({
        next: (response) => {
          const effectiveCartId = response?.data?.cartid || this.cartId;
          console.log('✅ Carrito preparado para Payphone:', response?.data);

          if (response?.data?.renewed) {
            console.log('🔁 Cart renovado:', response.data.originalCartId, '->', effectiveCartId);
          }

          this.refreshCartData();
          finalizeFacturacionData(effectiveCartId);
        },
        error: (error) => {
          console.error('❌ Error al preparar carrito para Payphone:', error);
          alert('Error al preparar el pago. Por favor, intente nuevamente.');
          this.selectedMetodoPago = null;
        }
      });
      return;
    }

    // Flujo legacy para métodos no pasarela
    this.generatePaymentId().then(paymentId => {
      finalizeFacturacionData(paymentId || this.cartId);
    }).catch(error => {
      console.error('❌ Error al generar payment ID:', error);
      alert('Error al procesar el pago. Por favor, intente nuevamente.');
      this.selectedMetodoPago = null;
    });
  }

  returnToForms() {
    this.selectedMetodoPago = null;
    this.rendered = false;
    this.comisionMetodoPago = 0;
    this.porcentajeComision = 0;
    this.totalConComision = 0;
    const container = document.getElementById('pp-button');
    if (container) {
      container.innerHTML = '';
    }
  }

  cancelCheckout() {
    console.log('❌ Cancelando checkout...');
    // Limpiar todo antes de navegar
    this.clearCountdown();

    // Limpiar PayPhone si está activo
    if (this.paymentBox) {
      try {
        if (typeof this.paymentBox.destroy === 'function') {
          this.paymentBox.destroy();
        }
        this.paymentBox = null;
      } catch (error) {
        console.error('Error al destruir PayPhone:', error);
      }
    }

    const container = document.getElementById('pp-button');
    if (container) {
      container.innerHTML = '';
    }

    this.rendered = false;
    if (this.urlSlug) {
      this.router.navigate(['/evento', this.urlSlug]);
    } else {
      this.router.navigate(['/']);
    }
  }

  private startCountdown(expiresAt: string): void {
    this.clearCountdown();
    this.updateTimeRemaining(expiresAt);
    this.countdownInterval = setInterval(() => {
      this.updateTimeRemaining(expiresAt);
    }, 1000);
  }

  private updateTimeRemaining(expiresAt: string): void {
    const now = new Date().getTime();
    const expiry = new Date(expiresAt).getTime();
    const diff = expiry - now;

    if (diff <= 0) {
      this.timeRemaining = 'Expirado';
      this.clearCountdown();
      // Mostrar alerta con SweetAlert2 cuando expire
      setTimeout(() => {
        Swal.fire({
          icon: 'warning',
          title: 'Tiempo expirado',
          text: 'El tiempo de reserva ha expirado. Redirigiendo a la página principal.',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#e8343f',
          allowOutsideClick: false
        }).then(() => {
          this.router.navigate(['/']);
        });
      }, 1000);
      return;
    }

    // Calcular horas, minutos y segundos
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (hours > 0) {
      this.timeRemaining = `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else {
      this.timeRemaining = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    // Forzar detección de cambios para actualizar la vista
    this.cdr.detectChanges();
  }

  private clearCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }
  pagarWIthPAYPHONE() {
    console.log('🔘 Iniciando carga del botón de PayPhone...');
    this.loadPayPhoneButton();
  }
}
